<template>
  <div class="max-vh-100 m-2 p-3">
    <!-- Toolbar -->
    <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
      <button class="btn-refresh" @click="refreshTable()"><i class="material-icons text-sm align-middle">refresh</i> Refresh</button>

      <!-- Date range -->
      <div class="d-flex align-items-center gap-1">
        <label class="text-xs text-secondary mb-0">From</label>
        <input type="date" class="form-control form-control-sm log-date-input" v-model="startDate" />
      </div>
      <div class="d-flex align-items-center gap-1">
        <label class="text-xs text-secondary mb-0">To</label>
        <input type="date" class="form-control form-control-sm log-date-input" v-model="endDate" />
      </div>
      <button class="btn btn-sm btn-primary m-0" @click="applyDateRange">
        <i class="material-icons text-sm align-middle me-1">filter_list</i>Apply
      </button>

      <!-- Auto-refresh -->
      <div class="d-flex align-items-center gap-2 ms-auto">
        <div class="d-flex align-items-center">
          <label class="text-xs text-secondary mb-0 me-1">
            <i class="material-icons text-sm align-middle">timer</i> Auto-refresh
          </label>
          <div class="form-check form-switch mb-0">
            <input class="form-check-input" type="checkbox" v-model="autoRefreshEnabled" @change="toggleAutoRefresh" />
          </div>
        </div>
        <select v-if="autoRefreshEnabled" class="form-select form-select-sm log-interval-select" v-model.number="autoRefreshInterval" @change="restartAutoRefresh">
          <option :value="30">30 sec</option>
          <option :value="60">60 sec</option>
          <option :value="90">90 sec</option>
          <option :value="180">3 min</option>
          <option :value="300">5 min</option>
        </select>
        <span v-if="autoRefreshEnabled" class="text-xs text-muted">
          <i class="material-icons" style="font-size:14px;vertical-align:middle;">schedule</i>
          {{ formatCountdown(autoRefreshCountdown) }}
        </span>
      </div>
    </div>

    <!-- Search Field Selector -->
    <div class="d-flex align-items-center gap-2 mb-2">
      <label class="text-xs text-secondary mb-0 fw-bold">Search in</label>
      <select class="form-select form-select-sm log-field-select" v-model="searchField">
        <option value="all">All Fields</option>
        <option value="message">Message</option>
        <option value="identity">Identity</option>
        <option value="trackid">TrackId</option>
        <option value="pid">ProcessId</option>
        <option value="priority">Priority</option>
      </select>
    </div>

    <div class="d-flex align-items-center gap-2 mb-3">
      <label class="text-xs text-secondary mb-0 fw-bold">Exclude identities</label>
      <select class="form-select form-select-sm log-field-select" v-model="excludedIdentities" multiple size="3" @change="applyIdentityFilter">
        <option v-for="identity in identityFilterOptions" :key="identity" :value="identity">{{ identity }}</option>
      </select>
    </div>
    <div class="d-flex align-items-center gap-2 mb-3">
      <label class="text-xs text-secondary mb-0 fw-bold">Filter priorities</label>
      <select class="form-select form-select-sm log-priority-select" v-model="selectedPriorities" multiple size="4" @change="applyPriorityFilter"
        aria-label="Filter logs by priority">
        <option v-for="priority in priorityOptions" :key="priority.value" :value="priority.value">
          {{ priority.value }} - {{ priority.label }}
        </option>
      </select>
      <span class="text-xs text-muted">No selection shows all levels.</span>
    </div>

    <section class="log-priority-summary mb-3" aria-label="Log counts by priority">
      <div class="log-priority-summary-heading">
        <h6 class="mb-1">Log counts by priority</h6>
        <p class="text-xs text-muted mb-2">
          Counts reflect the current date, search, and identity filters across all pages, before priority selection.
        </p>
      </div>
      <div class="log-priority-counts">
        <div v-for="priority in priorityOptions" :key="priority.value" class="log-priority-count">
          <span>{{ priority.value }} - {{ priority.label }}</span>
          <strong>{{ priorityCounts[priority.value] ?? "—" }}</strong>
        </div>
      </div>
    </section>

    <ReusableTable heading="Logs" :rows="logs" :columns="columns" idKey="logid" searchable :selectable="false"
      :loading="loading" :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
      @searchChanged="onSearchChanged">
      <template #col-priority="{ row }">
        {{ formatPriority(row.priority) }}
      </template>
      <template #col-message="{ row }">
        <div style="max-width: 300px; word-wrap: break-word; white-space: normal;">
          {{ row.message }}
        </div>
      </template>
      <template #actions="{ row }">
        <button class="btn btn-sm btn-outline-info my-0" @click="openLogDetails(row)">
          <i class="material-icons text-sm align-middle me-1">info</i>
          Read More
        </button>
      </template>
    </ReusableTable>

    <!-- Log Details Modal -->
    <ContentModal v-model="showModal" title="Log Details" size="lg">
      <template #default>
        <div v-if="selectedLog" class="log-details">
          <dl class="log-details-list">
            <div class="log-item">
              <dt>Log ID</dt>
              <dd>{{ selectedLog.logid }}</dd>
            </div>

            <div class="log-item">
              <dt>Priority</dt>
              <dd>{{ formatPriority(selectedLog.priority) }}</dd>
            </div>

            <div class="log-item">
              <dt>Message</dt>
              <dd class="log-message">
                {{ selectedLog.message }}
              </dd>
            </div>

            <div class="log-item">
              <dt>Timestamp</dt>
              <dd>{{ selectedLog.timestamp }}</dd>
            </div>

            <div class="log-item">
              <dt>Identity</dt>
              <dd>{{ selectedLog.identity }}</dd>
            </div>

            <div class="log-item">
              <dt>Track ID</dt>
              <dd>{{ selectedLog.trackid }}</dd>
            </div>

            <div class="log-item">
              <dt>Process ID</dt>
              <dd>{{ selectedLog.pid }}</dd>
            </div>
          </dl>
        </div>
      </template>
    </ContentModal>

  </div>
</template>

<script setup>
import { onMounted, onUnmounted, computed, ref } from "vue";
import { useStore } from "vuex";
import ReusableTable from "@/components/Table/Table.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import { formatDateTime } from "../../utils/FormatDate";

const store = useStore();

const loading = ref(false);
const showModal = ref(false);
const selectedLog = ref(null);
const searchTerm = ref("");
const searchField = ref("all");
const identityFilterOptions = ['lead_process', 'schedule_generator', 'webuser', 'icaluser', 'unsubscribe'];
const excludedIdentities = ref(['lead_process', 'schedule_generator', 'icaluser']);
const selectedPriorities = ref([]);
const priorityOptions = [
  { value: 0, label: "Emergency" },
  { value: 1, label: "Alert" },
  { value: 2, label: "Critical" },
  { value: 3, label: "Error" },
  { value: 4, label: "Warning" },
  { value: 5, label: "Notice" },
  { value: 6, label: "Info" },
  { value: 7, label: "Debug" },
];

// Date range (default: last 45 days)
function get45DaysAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 45);
  return d.toISOString().slice(0, 10);
}
function getToday() {
  return new Date().toISOString().slice(0, 10);
}
const startDate = ref(get45DaysAgo());
const endDate = ref(getToday());

// Auto-refresh
const autoRefreshEnabled = ref(false);
const autoRefreshInterval = ref(60); // seconds
const autoRefreshCountdown = ref(0);
let refreshTimer = null;
let countdownTimer = null;

const userTimezone = computed(() => store.getters.stSessionData?.timezone)

const logs = computed(() =>
  store.getters.allLogs.map((log) => ({
    ...log,
    timestamp: formatDateTime(log.timestamp, userTimezone.value),
  }))
);

const pagination = computed(() => store.getters.logsPagination);
const priorityCounts = computed(() => store.getters.logsPriorityCounts || {});

const columns = [
  { key: "logid", label: "LogId" },
  { key: "priority", label: "Priority" },
  { key: "message", label: "Message" },
  { key: "timestamp", label: "TimeStamp" },
  { key: "identity", label: "Identity" },
  { key: "trackid", label: "TrackId" },
  { key: "pid", label: "ProcessId" },
];

const openLogDetails = (row) => {
  selectedLog.value = row;
  showModal.value = true;
};

// Fetch logs with pagination
const fetchLogs = async (page = 1, pageSize = 10) => {
  loading.value = true;
  try {
    await store.dispatch("getLogs", {
      page,
      pageSize,
      search: searchTerm.value || "",
      search_field: searchField.value || "all",
      start_date: startDate.value || "",
      end_date: endDate.value || "",
      exclude_identity: excludedIdentities.value,
      priorities: selectedPriorities.value,
    });
  } catch (error) {
    console.error("Error fetching logs:", error);
  } finally {
    loading.value = false;
  }
};

// Pagination handlers
const onChangePage = (page) => {
  if (!page || page < 1) return;
  fetchLogs(page, pagination.value.pageSize || 10);
};

const onUpdatePageSize = (size) => {
  if (!size || size < 1) return;
  fetchLogs(1, size);
};

const onSearchChanged = (term) => {
  searchTerm.value = term;
  fetchLogs(1, pagination.value.pageSize || 10);
};

const refreshTable = () => {
  fetchLogs(pagination.value.currentPage || 1, pagination.value.pageSize || 10);
};

const applyDateRange = () => {
  fetchLogs(1, pagination.value.pageSize || 10);
};

const applyIdentityFilter = () => {
  fetchLogs(1, pagination.value.pageSize || 10);
};
const applyPriorityFilter = () => {
  fetchLogs(1, pagination.value.pageSize || 10);
};

const formatPriority = (priority) => {
  const value = Number(priority);
  const level = priorityOptions.find((option) => option.value === value);
  return level ? `${value} - ${level.label}` : priority;
};

function toggleAutoRefresh() {
  if (autoRefreshEnabled.value) {
    startAutoRefresh();
  } else {
    stopAutoRefresh();
  }
}

function startAutoRefresh() {
  stopAutoRefresh();
  autoRefreshCountdown.value = autoRefreshInterval.value;
  countdownTimer = setInterval(() => {
    autoRefreshCountdown.value--;
    if (autoRefreshCountdown.value <= 0) {
      autoRefreshCountdown.value = autoRefreshInterval.value;
    }
  }, 1000);
  refreshTimer = setInterval(() => {
    refreshTable();
    autoRefreshCountdown.value = autoRefreshInterval.value;
  }, autoRefreshInterval.value * 1000);
}

function stopAutoRefresh() {
  if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null; }
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
  autoRefreshCountdown.value = 0;
}

function restartAutoRefresh() {
  if (autoRefreshEnabled.value) startAutoRefresh();
}

function formatCountdown(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

onMounted(async () => {
  await fetchLogs(1, 10);
});

onUnmounted(() => {
  stopAutoRefresh();
});
</script>

<style scoped>
.btn-refresh {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 6px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.btn-refresh:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.55);
}
.btn-refresh:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
}
.log-date-input {
  max-width: 145px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  font-size: 0.82rem;
}
.log-interval-select {
  max-width: 100px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  font-size: 0.82rem;
}
.log-field-select {
  max-width: 160px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  font-size: 0.82rem;
}
.log-priority-select {
  max-width: 190px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  font-size: 0.82rem;
}
.log-priority-summary {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 12px 14px;
  background: #fff;
}
.log-priority-counts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 8px;
}
.log-priority-count {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-radius: 6px;
  padding: 7px 9px;
  background: #f8f9fa;
  font-size: 0.8rem;
}
.log-priority-count strong {
  font-size: 0.9rem;
}
</style>

<style>
.log-details-list {
  margin: 0;
  padding: 0;
}

.log-item {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.log-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.log-item dt {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.log-item dd {
  margin: 0;
  color: #212529;
  font-size: 0.95rem;
  word-wrap: break-word;
}

.log-message {
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 300px;
  overflow-y: auto;
}
</style>